import * as signalR from '@microsoft/signalr';
import * as Sentry from '@sentry/react';
import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { downloadFileFromUrl } from 'src/utils/fileUtils';
import { FileExportResult } from 'src/WebApiController';

import { useAppContext } from '../AppContext';
import { useClientLogContext } from '../ClientLogContext';
import { POSSignalRHub } from '../SignalRConnection';

type IFileExportContext = {
  fileExportIsPending: boolean;
  setFileExportIsPending: (value: boolean, timeoutSecs?: number) => void;
  fileExportHub: POSSignalRHub | undefined;
};

const DEFAULT_MAX_TIMEOUT_SECS = 600;
const ON_FILE_RESULT_METHOD = 'on-file-export-result';

export const FileExportContext = React.createContext<IFileExportContext>({
  fileExportIsPending: false,
  setFileExportIsPending: () => {},
  fileExportHub: undefined,
});

export const useFileExportContext = () => useContext(FileExportContext);

export const FileExportContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { activeAccountWebClientConfig } = useAppContext();

  const [fileExportIsPending, setFileExportIsPending] = React.useState(false);
  const { showErrorDialog } = useClientLogContext();

  const { logEvent } = useClientLogContext();
  const hubConnection = useRef<POSSignalRHub>();

  useEffect(() => {
    if (!hubConnection.current) {
      hubConnection.current = new POSSignalRHub(
        '/hubs/fileexport',
        activeAccountWebClientConfig,
        logEvent
      );

      const fileHandler = async (message: FileExportResult) => {
        try {
          const fileName = message.fileName;
          const fileUri = message.fileUri;
          if (message.isSuccess) {
            downloadFileFromUrl(fileName!, fileUri!);
          } else {
            showErrorDialog(
              'FileExport',
              {
                message: message.errorMessage,
              },
              {
                trackErrorData: {
                  message,
                },
              }
            );
            console.error('FileExport - error: ' + message.errorMessage);
          }
          setFileExportIsPending(false);
        } catch (e) {
          console.error('FileExport - malformed SignalR Message: ' + message);

          try {
            Sentry.captureException(e);
          } catch (err) {
            console.warn('Sentry log request failed', err);
          }
        } finally {
          setFileExportIsPending(false);
        }
      };

      hubConnection.current.on(ON_FILE_RESULT_METHOD, fileHandler);

      hubConnection.current.start().catch(console.error);

      console.debug(
        'Creating new FileExport connection with id: ' +
          hubConnection.current.connectionId
      );
    }

    // Close the connection before unmount
    return () => {
      if (
        hubConnection.current &&
        hubConnection.current.state === signalR.HubConnectionState.Connected
      ) {
        console.debug(
          'Stopping FileExport connection with id: ' +
            hubConnection.current.connectionId
        );
        hubConnection.current.stop();
        hubConnection.current = undefined;
      }
    };
  }, [activeAccountWebClientConfig, logEvent, showErrorDialog]);

  const setFileExportIsPendingWithTimeout = useCallback(
    (value: boolean, timeoutSecs?: number) => {
      setFileExportIsPending(value);

      if (value) {
        setTimeout(
          () => {
            setFileExportIsPending(false);
          },
          (timeoutSecs ?? DEFAULT_MAX_TIMEOUT_SECS) * 1000
        );
      }
    },
    []
  );

  return (
    <FileExportContext.Provider
      value={{
        fileExportIsPending,
        setFileExportIsPending: setFileExportIsPendingWithTimeout,
        fileExportHub: hubConnection.current,
      }}
    >
      {children}
    </FileExportContext.Provider>
  );
};
